/**
 * TrpcAccountDeletionAdapter — the production `AccountDeletionPort` over the
 * typed tRPC client (033 US2, T029). Calls the live
 * `member.myData.requestDeletion({ confirm: true })`: the member's cookie
 * session authenticates the same-origin request and `memberProcedure` on the
 * API side guarantees only a member may erase (and only their own account —
 * the API derives the target from the session, never the payload).
 *
 * Error mapping (the `TrpcContentReader` discriminator pattern):
 *   - the API's P7 refusal — `TRPCError({ code: 'PRECONDITION_FAILED',
 *     message: 'erasure-unavailable-unconfigured-retention' })` while the
 *     audit-retention window (DL-009) has no authorized value — becomes
 *     `ErasureUnavailableError`, which the confirm dialog renders as the calm
 *     "not available yet" state (distinct from a transient failure).
 *   - every other error rethrows unchanged (the dialog's retryable path).
 *
 * The `member` namespace is OPTIONAL on the published `AppRouter` (the
 * conditional-spread posture — production mounts it, some test compositions
 * omit it), so the adapter narrows before calling and fails loudly if wired
 * against a router without the namespace (a composition error, not a runtime
 * condition — loud > silent, P7).
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 */

import { TRPCClientError } from '@trpc/client';

import type { WebTrpcClient } from '../trpc/client.js';
import { type AccountDeletionPort, ErasureUnavailableError } from './AccountDeletionPort.js';

/** The API's stable machine-readable refusal message (request-deletion.ts). */
const UNCONFIGURED_RETENTION_MESSAGE = 'erasure-unavailable-unconfigured-retention';

export class TrpcAccountDeletionAdapter implements AccountDeletionPort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async requestDeletion(): Promise<{ completedAt: string }> {
    const member = this.#client.member;
    // Both `member` and `member.myData` are optional on the published router
    // (each member sub-surface mounts independently). Absence is a composition
    // error, not a runtime condition — loud > silent (P7).
    if (member?.myData === undefined) {
      throw new Error(
        'TrpcAccountDeletionAdapter: member.myData is absent on the AppRouter — ' +
          'account erasure requires the 033 US2 API activation.',
      );
    }
    try {
      return await member.myData.requestDeletion.mutate({ confirm: true });
    } catch (err) {
      // The structural `data.code` discriminator, guarded by an
      // `instanceof TRPCClientError` so a non-tRPC error that happens to
      // carry a `data.code` by coincidence is not converted (the
      // TrpcContentReader convention).
      if (
        err instanceof TRPCClientError &&
        err.data?.code === 'PRECONDITION_FAILED' &&
        err.message === UNCONFIGURED_RETENTION_MESSAGE
      ) {
        throw new ErasureUnavailableError();
      }
      throw err;
    }
  }
}
